<template>
  <v-container fluid class="settings-page">
    <div class="settings-page__heading">
      <div>
        <div class="settings-page__eyebrow">Administration</div>
        <h1>Settings</h1>
        <p>Manage your institute, users, payment services, products, and operational tools.</p>
      </div>
    </div>

    <section v-for="section in visibleSections" :key="section.title" class="settings-section">
      <div class="settings-section__heading">
        <div class="settings-section__icon"><v-icon color="primary" size="20">{{ section.icon }}</v-icon></div>
        <div><h2>{{ section.title }}</h2><p>{{ section.description }}</p></div>
      </div>

      <v-row>
        <v-col v-for="item in section.items" :key="item.title" cols="12" sm="6" lg="4">
          <button type="button" class="settings-link" @click="open(item.path)">
            <span class="settings-link__icon"><v-icon color="primary" size="23">{{ item.icon }}</v-icon></span>
            <span class="settings-link__copy"><strong>{{ item.title }}</strong><small>{{ item.description }}</small></span>
            <v-icon class="settings-link__arrow" size="19">mdi-chevron-right</v-icon>
          </button>
        </v-col>
      </v-row>
    </section>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "settings-home",
  computed: {
    ...mapGetters("global", ["checkRightStatus"]),
    sections() {
      return [
        {
          title: "Users",
          description: "Create accounts and control access to the portal.",
          icon: "mdi-account-group-outline",
          items: [
            { title: "New User", description: "Create a portal account and assign permissions.", icon: "mdi-account-plus-outline", path: "/user/create", right: 97 },
            { title: "User List", description: "Review users, roles, status, and permissions.", icon: "mdi-account-multiple-outline", path: "/user/list" },
          ],
        },
        {
          title: "Institute",
          description: "Maintain the core information and preferences for your institute.",
          icon: "mdi-office-building-cog-outline",
          items: [
            { title: "General Settings", description: "Update institute details, branding, and configuration.", icon: "mdi-cog-outline", path: "/setting" },
          ],
        },
        {
          title: "Payment Services",
          description: "Configure payment processing and donation categories.",
          icon: "mdi-credit-card-settings-outline",
          items: [
            { title: "Merchant List", description: "Manage payment processor and merchant accounts.", icon: "mdi-credit-card-outline", path: "/merchant", right: 90 },
            { title: "Donation Categories", description: "Organize the categories available for donations.", icon: "mdi-hand-coin-outline", path: "/donationCategory", right: 86 },
          ],
        },
        {
          title: "Products",
          description: "Manage products used in billing and transactions.",
          icon: "mdi-package-variant-closed",
          items: [
            { title: "Product List", description: "Review and maintain available products.", icon: "mdi-format-list-bulleted-square", path: "/product/list", right: 94 },
          ],
        },
        {
          title: "Operations",
          description: "Configure counters and other daily operational tools.",
          icon: "mdi-tune-variant",
          items: [
            { title: "Counter List", description: "Manage counters used throughout the institute.", icon: "mdi-counter", path: "/counter" },
          ],
        },
      ];
    },
    visibleSections() {
      return this.sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => !item.right || this.checkRightStatus(item.right)),
        }))
        .filter((section) => section.items.length);
    },
  },
  created() {
    document.title = "taleem - Settings";
  },
  methods: {
    open(path) {
      this.$router.push(path);
    },
  },
};
</script>

<style scoped>
.settings-page { min-height: 100%; padding: 30px 36px 48px; background: #f6f8f6; }
.settings-page__heading { margin-bottom: 24px; }
.settings-page__eyebrow { margin-bottom: 5px; color: var(--v-primary-base); font-size: 11px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
.settings-page__heading h1 { margin: 0; color: #172033; font-size: 29px; line-height: 1.2; letter-spacing: -.025em; }
.settings-page__heading p { max-width: 680px; margin: 6px 0 0; color: #728095; font-size: 13px; }
.settings-section { margin-bottom: 18px; padding: 22px 24px 10px; border: 1px solid #e3e9e3; border-radius: 13px; background: #fff; }
.settings-section__heading { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.settings-section__icon { display: grid; width: 40px; height: 40px; flex: 0 0 40px; place-items: center; border-radius: 10px; background: #edf7ee; }
.settings-section__heading h2 { margin: 0 0 2px; color: #2d4034; font-size: 17px; }
.settings-section__heading p { margin: 0; color: #879388; font-size: 11px; }
.settings-link { display: flex; width: 100%; min-height: 86px; padding: 15px 16px; align-items: center; gap: 13px; border: 1px solid #e4eae5; border-radius: 11px; color: inherit; background: #fbfcfb; text-align: left; cursor: pointer; transition: border-color .16s, box-shadow .16s, transform .16s; }
.settings-link:hover, .settings-link:focus-visible { border-color: #a9d5ad; outline: none; background: #fff; box-shadow: 0 7px 18px rgba(40, 74, 44, .08); transform: translateY(-1px); }
.settings-link__icon { display: grid; width: 44px; height: 44px; flex: 0 0 44px; place-items: center; border-radius: 11px; background: #eaf6eb; }
.settings-link__copy { min-width: 0; flex: 1; }
.settings-link__copy strong, .settings-link__copy small { display: block; }
.settings-link__copy strong { color: #2c3b31; font-size: 14px; }
.settings-link__copy small { margin-top: 4px; color: #7f8c82; font-size: 10px; line-height: 1.4; }
.settings-link__arrow { color: #a1aba3; }
@media (max-width: 700px) {
  .settings-page { padding: 20px 12px 36px; }
  .settings-page__heading h1 { font-size: 25px; }
  .settings-section { padding: 18px 14px 6px; }
  .settings-section__heading { align-items: flex-start; }
  .settings-link { min-height: 78px; }
}
</style>
